import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Save, Zap, Loader2, Copy, KeyRound, Globe, Network, Plus, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api } from '@/lib/api';

import { useBrandName } from '@/lib/branding';
const ROLES = ['viewer', 'analyst', 'editor', 'admin', 'tenant_admin'];

const blank = {
  email_domains: [], default_role: 'viewer', group_role_map: {},
  saml: { enabled: false, idp_entity_id: '', idp_sso_url: '', idp_x509cert: '', attr_email: 'email', attr_name: 'name', attr_groups: 'groups', nameid_format: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress', authn_requests_signed: false },
  oidc: { enabled: false, issuer: '', client_id: '', client_secret: '', scopes: 'openid email profile', attr_groups: 'groups' },
  ldap: { enabled: false, host: '', port: 389, use_ssl: false, bind_dn: '', bind_password: '', base_dn: '', user_filter: '(mail={email})', email_attr: 'mail', name_attr: 'cn', group_attr: 'memberOf' },
};

export default function SsoSettingsPage() {
  const APP = useBrandName();
  const [cfg, setCfg] = useState(blank);
  const [endpoints, setEndpoints] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getSsoConfig();
      setEndpoints(res.endpoints || {});
      if (res.config) setCfg({ ...blank, ...res.config, saml: { ...blank.saml, ...res.config.saml }, oidc: { ...blank.oidc, ...res.config.oidc }, ldap: { ...blank.ldap, ...res.config.ldap } });
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setProto = (proto, patch) => setCfg(c => ({ ...c, [proto]: { ...c[proto], ...patch } }));
  const addDomain = () => {
    const d = domainInput.trim().toLowerCase();
    if (d && !cfg.email_domains.includes(d)) setCfg(c => ({ ...c, email_domains: [...c.email_domains, d] }));
    setDomainInput('');
  };
  const removeDomain = (d) => setCfg(c => ({ ...c, email_domains: c.email_domains.filter(x => x !== d) }));

  const save = async () => {
    setSaving(true);
    try { await api.updateSsoConfig(cfg); toast.success('SSO settings saved'); await load(); }
    catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const test = async (proto) => {
    setTesting(proto);
    try { const r = await api.testSso(proto, cfg[proto]); setTestResult(t => ({ ...t, [proto]: r })); }
    catch (e) { setTestResult(t => ({ ...t, [proto]: { success: false, message: e.message } })); }
    finally { setTesting(null); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success('Copied'); };

  // group_role_map row editing
  const grm = Object.entries(cfg.group_role_map || {});
  const setGrm = (entries) => setCfg(c => ({ ...c, group_role_map: Object.fromEntries(entries.filter(([k]) => k)) }));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const TestBadge = ({ proto }) => {
    const r = testResult[proto];
    if (!r) return null;
    return (
      <div className={`flex items-center gap-1 text-xs mt-2 ${r.success ? 'text-emerald-500' : 'text-red-500'}`} data-testid={`sso-test-result-${proto}`}>
        {r.success ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}{r.message}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="sso-settings-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-primary" /> Enterprise SSO</h1>
          <p className="text-sm text-muted-foreground mt-1">Connect your identity provider. Users from the domains below sign in via SSO; new users are auto-provisioned.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="sso-save-btn">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save</Button>
      </div>

      <Card className="p-4 mb-5 space-y-4">
        <div>
          <Label>Email domains (route these to SSO)</Label>
          <div className="flex gap-2 mt-1">
            <Input value={domainInput} onChange={e => setDomainInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addDomain())} placeholder="company.com" data-testid="sso-domain-input" />
            <Button variant="outline" onClick={addDomain} data-testid="sso-add-domain-btn"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {cfg.email_domains.map(d => (
              <span key={d} className="inline-flex items-center gap-1 bg-muted rounded-full px-3 py-1 text-xs" data-testid={`sso-domain-${d}`}>
                {d}<button onClick={() => removeDomain(d)} className="text-red-500"><Trash2 className="w-3 h-3" /></button>
              </span>
            ))}
            {cfg.email_domains.length === 0 && <span className="text-xs text-muted-foreground">No domains yet</span>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Default role for new users</Label>
            <select className="h-9 w-full rounded-md border bg-background text-sm px-2 mt-1" value={cfg.default_role} onChange={e => setCfg(c => ({ ...c, default_role: e.target.value }))} data-testid="sso-default-role">
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label>Group → role mapping (IdP group name → {APP} role)</Label>
          <div className="space-y-2 mt-1">
            {grm.map(([g, r], i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-6" placeholder="IdP group" value={g} onChange={e => { const next = [...grm]; next[i] = [e.target.value, r]; setGrm(next); }} />
                <select className="col-span-5 h-9 rounded-md border bg-background text-sm px-2" value={r} onChange={e => { const next = [...grm]; next[i] = [g, e.target.value]; setGrm(next); }}>
                  {ROLES.map(role => <option key={role} value={role}>{role}</option>)}
                </select>
                <button className="col-span-1 text-red-500" onClick={() => setGrm(grm.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setGrm([...grm, ['', 'viewer']])} data-testid="sso-add-grm-btn"><Plus className="w-3 h-3 mr-1" />Add mapping</Button>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="oidc">
        <TabsList>
          <TabsTrigger value="oidc" data-testid="sso-tab-oidc"><Globe className="w-4 h-4 mr-1" />OIDC / Azure AD</TabsTrigger>
          <TabsTrigger value="saml" data-testid="sso-tab-saml"><ShieldCheck className="w-4 h-4 mr-1" />SAML 2.0</TabsTrigger>
          <TabsTrigger value="ldap" data-testid="sso-tab-ldap"><Network className="w-4 h-4 mr-1" />LDAP / AD</TabsTrigger>
        </TabsList>

        {/* OIDC */}
        <TabsContent value="oidc">
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2"><Switch checked={cfg.oidc.enabled} onCheckedChange={v => setProto('oidc', { enabled: v })} data-testid="sso-oidc-enabled" /><Label className="!mb-0">Enable OIDC / OAuth2 (Microsoft Entra ID, Okta, Google, generic)</Label></div>
            <div><Label>Issuer URL</Label><Input value={cfg.oidc.issuer} onChange={e => setProto('oidc', { issuer: e.target.value })} placeholder="https://login.microsoftonline.com/<tenant>/v2.0" data-testid="sso-oidc-issuer" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Client ID</Label><Input value={cfg.oidc.client_id} onChange={e => setProto('oidc', { client_id: e.target.value })} data-testid="sso-oidc-clientid" /></div>
              <div><Label>Client Secret</Label><Input type="password" value={cfg.oidc.client_secret} onChange={e => setProto('oidc', { client_secret: e.target.value })} placeholder="leave blank to keep existing" data-testid="sso-oidc-secret" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Scopes</Label><Input value={cfg.oidc.scopes} onChange={e => setProto('oidc', { scopes: e.target.value })} /></div>
              <div><Label>Groups claim</Label><Input value={cfg.oidc.attr_groups} onChange={e => setProto('oidc', { attr_groups: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2 text-xs bg-muted rounded p-2"><span className="font-medium">Redirect URI:</span><code className="truncate flex-1">{endpoints.oidc_redirect}</code><button onClick={() => copy(endpoints.oidc_redirect)}><Copy className="w-3.5 h-3.5" /></button></div>
            <Button variant="outline" size="sm" onClick={() => test('oidc')} disabled={testing === 'oidc'} data-testid="sso-oidc-test">{testing === 'oidc' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}Test discovery</Button>
            <TestBadge proto="oidc" />
          </Card>
        </TabsContent>

        {/* SAML */}
        <TabsContent value="saml">
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2"><Switch checked={cfg.saml.enabled} onCheckedChange={v => setProto('saml', { enabled: v })} data-testid="sso-saml-enabled" /><Label className="!mb-0">Enable SAML 2.0 (Okta, OneLogin, ADFS, PingFederate)</Label></div>
            <div><Label>IdP Entity ID</Label><Input value={cfg.saml.idp_entity_id} onChange={e => setProto('saml', { idp_entity_id: e.target.value })} data-testid="sso-saml-entityid" /></div>
            <div><Label>IdP SSO URL</Label><Input value={cfg.saml.idp_sso_url} onChange={e => setProto('saml', { idp_sso_url: e.target.value })} data-testid="sso-saml-ssourl" /></div>
            <div><Label>IdP X.509 Certificate</Label><textarea value={cfg.saml.idp_x509cert} onChange={e => setProto('saml', { idp_x509cert: e.target.value })} className="w-full h-24 rounded-md border bg-background text-xs font-mono p-2" placeholder="-----BEGIN CERTIFICATE-----" data-testid="sso-saml-cert" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Email attr</Label><Input value={cfg.saml.attr_email} onChange={e => setProto('saml', { attr_email: e.target.value })} /></div>
              <div><Label>Name attr</Label><Input value={cfg.saml.attr_name} onChange={e => setProto('saml', { attr_name: e.target.value })} /></div>
              <div><Label>Groups attr</Label><Input value={cfg.saml.attr_groups} onChange={e => setProto('saml', { attr_groups: e.target.value })} /></div>
            </div>
            <div className="space-y-2 text-xs bg-muted rounded p-2">
              <div className="flex items-center gap-2"><span className="font-medium w-28">ACS URL:</span><code className="truncate flex-1">{endpoints.saml_acs}</code><button onClick={() => copy(endpoints.saml_acs)}><Copy className="w-3.5 h-3.5" /></button></div>
              <div className="flex items-center gap-2"><span className="font-medium w-28">SP Metadata:</span><code className="truncate flex-1">{endpoints.saml_metadata}</code><button onClick={() => copy(endpoints.saml_metadata)}><Copy className="w-3.5 h-3.5" /></button></div>
            </div>
            <p className="text-[11px] text-muted-foreground">Provide these URLs to your IdP admin when registering {APP} as a Service Provider.</p>
          </Card>
        </TabsContent>

        {/* LDAP */}
        <TabsContent value="ldap">
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2"><Switch checked={cfg.ldap.enabled} onCheckedChange={v => setProto('ldap', { enabled: v })} data-testid="sso-ldap-enabled" /><Label className="!mb-0">Enable LDAP / Active Directory</Label></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label>Host</Label><Input value={cfg.ldap.host} onChange={e => setProto('ldap', { host: e.target.value })} placeholder="ldap.company.com" data-testid="sso-ldap-host" /></div>
              <div><Label>Port</Label><Input type="number" value={cfg.ldap.port} onChange={e => setProto('ldap', { port: parseInt(e.target.value) || 389 })} /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={cfg.ldap.use_ssl} onCheckedChange={v => setProto('ldap', { use_ssl: v })} /><Label className="!mb-0">Use SSL/LDAPS</Label></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Bind DN</Label><Input value={cfg.ldap.bind_dn} onChange={e => setProto('ldap', { bind_dn: e.target.value })} placeholder="cn=svc,dc=company,dc=com" data-testid="sso-ldap-binddn" /></div>
              <div><Label>Bind Password</Label><Input type="password" value={cfg.ldap.bind_password} onChange={e => setProto('ldap', { bind_password: e.target.value })} placeholder="leave blank to keep existing" data-testid="sso-ldap-bindpw" /></div>
            </div>
            <div><Label>Base DN</Label><Input value={cfg.ldap.base_dn} onChange={e => setProto('ldap', { base_dn: e.target.value })} placeholder="dc=company,dc=com" data-testid="sso-ldap-basedn" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>User filter</Label><Input value={cfg.ldap.user_filter} onChange={e => setProto('ldap', { user_filter: e.target.value })} placeholder="(mail={email})" /></div>
              <div><Label>Group attribute</Label><Input value={cfg.ldap.group_attr} onChange={e => setProto('ldap', { group_attr: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email attribute</Label><Input value={cfg.ldap.email_attr} onChange={e => setProto('ldap', { email_attr: e.target.value })} /></div>
              <div><Label>Name attribute</Label><Input value={cfg.ldap.name_attr} onChange={e => setProto('ldap', { name_attr: e.target.value })} /></div>
            </div>
            <Button variant="outline" size="sm" onClick={() => test('ldap')} disabled={testing === 'ldap'} data-testid="sso-ldap-test">{testing === 'ldap' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />}Test bind</Button>
            <TestBadge proto="ldap" />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
